/**
 * Moving Cost Calculator Module
 * Calculates estimated moving costs based on user inputs
 */

// Pricing configuration
const PRICING = {
  // Services (per hour)
  services: {
    lkwWith2Helpers: 94.00,  // LKW + 2 helpers
    helperPerHour: 40.00     // Additional helper
  },

  // Materials
  materials: {
    umzugskartonNeu: 3.20,
    umzugskartonVerleih: 1.30,  // per day
    kleiderboxNeu: 16.00,
    kleiderboxVerleih: 5.00,    // per day
    luftpolsterfolie: 1.50,     // per meter
    seidenpapier: 3.50          // per kg
  },

  // Room-based time estimation (hours)
  roomTimeEstimates: {
    1: { min: 3, max: 4 },
    2: { min: 4, max: 6 },
    3: { min: 6, max: 8 },
    4: { min: 8, max: 10 },
    5: { min: 10, max: 12 },
    6: { min: 12, max: 14 }
  },

  // Distance surcharge (per km over base distance)
  distanceSurcharge: 1.50,
  baseDistance: 10  // km
};

class MovingCostCalculator {
  constructor(element) {
    this.calculator = element;
    this.form = this.calculator.querySelector('.calculator-form');
    this.resultDisplay = this.calculator.querySelector('.calculator-result');

    this.init();
  }

  init() {
    if (!this.form || !this.resultDisplay) return;

    // Bind input events
    const inputs = this.form.querySelectorAll('input, select');
    inputs.forEach(input => {
      input.addEventListener('change', () => this.calculate());
      input.addEventListener('input', () => this.calculate());
    });

    // Initial calculation
    this.calculate();
  }

  /**
   * Get form values
   */
  getFormValues() {
    return {
      rooms: parseInt(this.form.querySelector('[name="rooms"]')?.value) || 1,
      distance: parseFloat(this.form.querySelector('[name="distance"]')?.value) || 0,
      floors: parseInt(this.form.querySelector('[name="floors"]')?.value) || 0,
      elevator: this.form.querySelector('[name="elevator"]')?.checked || false,
      helpers: parseInt(this.form.querySelector('[name="helpers"]')?.value) || 2,

      // Materials
      umzugskartons: parseInt(this.form.querySelector('[name="umzugskartons"]')?.value) || 0,
      kleiderboxen: parseInt(this.form.querySelector('[name="kleiderboxen"]')?.value) || 0,
      luftpolsterfolie: parseFloat(this.form.querySelector('[name="luftpolsterfolie"]')?.value) || 0,

      // Material type (buy or rent)
      materialType: this.form.querySelector('[name="material_type"]')?.value || 'buy'
    };
  }

  /**
   * Calculate total moving cost
   */
  calculate() {
    const values = this.getFormValues();
    let breakdown = {
      service: 0,
      materials: 0,
      distance: 0,
      floors: 0
    };

    // 1. Estimate time based on rooms
    const timeEstimate = this.estimateTime(values.rooms, values.floors, values.elevator);

    // 2. Calculate service cost (LKW + helpers)
    breakdown.service = PRICING.services.lkwWith2Helpers * timeEstimate.hours;

    // Additional helpers (beyond the 2 included with LKW)
    if (values.helpers > 2) {
      const additionalHelpers = values.helpers - 2;
      breakdown.service += additionalHelpers * PRICING.services.helperPerHour * timeEstimate.hours;
    }

    // 3. Calculate distance surcharge
    if (values.distance > PRICING.baseDistance) {
      const extraKm = values.distance - PRICING.baseDistance;
      breakdown.distance = extraKm * PRICING.distanceSurcharge;
    }

    // 4. Calculate floor surcharge (no elevator)
    if (!values.elevator && values.floors > 0) {
      breakdown.floors = values.floors * 20; // €20 per floor without elevator
    }

    // 5. Calculate materials cost
    if (values.materialType === 'buy') {
      breakdown.materials += values.umzugskartons * PRICING.materials.umzugskartonNeu;
      breakdown.materials += values.kleiderboxen * PRICING.materials.kleiderboxNeu;
    } else {
      // Rental (assume 7 days)
      const rentalDays = 7;
      breakdown.materials += values.umzugskartons * PRICING.materials.umzugskartonVerleih * rentalDays;
      breakdown.materials += values.kleiderboxen * PRICING.materials.kleiderboxVerleih * rentalDays;
    }
    breakdown.materials += values.luftpolsterfolie * PRICING.materials.luftpolsterfolie;

    // Total
    const total = Object.values(breakdown).reduce((sum, val) => sum + val, 0);

    // Update display
    this.updateDisplay({
      total,
      breakdown,
      timeEstimate,
      values
    });
  }

  /**
   * Estimate moving time based on rooms and other factors
   */
  estimateTime(rooms, floors, hasElevator) {
    const roomKey = Math.min(rooms, 6);
    const baseEstimate = PRICING.roomTimeEstimates[roomKey] || { min: 14, max: 16 };

    let hours = (baseEstimate.min + baseEstimate.max) / 2;

    // Add time for floors without elevator
    if (!hasElevator && floors > 0) {
      hours += floors * 0.5; // 30 min per floor
    }

    return {
      hours: Math.ceil(hours),
      min: baseEstimate.min,
      max: baseEstimate.max
    };
  }

  /**
   * Update result display
   */
  updateDisplay(result) {
    const { total, breakdown, timeEstimate, values } = result;

    // Format currency
    const formatEur = (amount) => {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2
      }).format(amount);
    };

    // Build result HTML
    let html = `
      <div class="result-header">
        <h3>Geschätzte Gesamtkosten</h3>
        <div class="total-price">${formatEur(total)}</div>
        <p class="result-note">Unverbindliche Schätzung inkl. 19% MwSt.</p>
      </div>

      <div class="result-breakdown">
        <h4>Kostenaufschlüsselung</h4>
        <table class="breakdown-table">
          <tbody>
            <tr>
              <td>
                <strong>Umzugsservice</strong>
                <div class="breakdown-details">
                  LKW + ${values.helpers} Helfer × ${timeEstimate.hours} Std.
                  ${values.helpers > 2 ? `<br>(inkl. ${values.helpers - 2} zusätzliche Helfer)` : ''}
                </div>
              </td>
              <td class="breakdown-price">${formatEur(breakdown.service)}</td>
            </tr>
    `;

    if (breakdown.distance > 0) {
      const extraKm = values.distance - PRICING.baseDistance;
      html += `
            <tr>
              <td>
                <strong>Entfernungszuschlag</strong>
                <div class="breakdown-details">${extraKm} km × ${formatEur(PRICING.distanceSurcharge)}/km</div>
              </td>
              <td class="breakdown-price">${formatEur(breakdown.distance)}</td>
            </tr>
      `;
    }

    if (breakdown.floors > 0) {
      html += `
            <tr>
              <td>
                <strong>Etagenzuschlag</strong>
                <div class="breakdown-details">${values.floors} Etagen ohne Aufzug</div>
              </td>
              <td class="breakdown-price">${formatEur(breakdown.floors)}</td>
            </tr>
      `;
    }

    if (breakdown.materials > 0) {
      const materialTypeText = values.materialType === 'buy' ? 'Kauf' : 'Miete (7 Tage)';
      html += `
            <tr>
              <td>
                <strong>Umzugsmaterial (${materialTypeText})</strong>
                <div class="breakdown-details">
                  ${values.umzugskartons > 0 ? `${values.umzugskartons} Umzugskartons` : ''}
                  ${values.kleiderboxen > 0 ? `<br>${values.kleiderboxen} Kleiderboxen` : ''}
                  ${values.luftpolsterfolie > 0 ? `<br>${values.luftpolsterfolie}m Luftpolsterfolie` : ''}
                </div>
              </td>
              <td class="breakdown-price">${formatEur(breakdown.materials)}</td>
            </tr>
      `;
    }

    html += `
          </tbody>
          <tfoot>
            <tr class="total-row">
              <td><strong>Gesamtsumme</strong></td>
              <td class="breakdown-price"><strong>${formatEur(total)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="result-footer">
        <p class="disclaimer">
          <strong>Hinweis:</strong> Dies ist eine unverbindliche Schätzung. Der tatsächliche Preis kann je nach
          spezifischen Anforderungen, Zugänglichkeit und zusätzlichen Services variieren.
          Für ein genaues Angebot kontaktieren Sie uns bitte.
        </p>
        <div class="result-actions">
          <a href="/kontakt" class="btn btn-primary">Unverbindliches Angebot anfordern</a>
        </div>
      </div>
    `;

    this.resultDisplay.innerHTML = html;

    // Show result with animation
    if (!this.resultDisplay.classList.contains('visible')) {
      this.resultDisplay.classList.add('visible');

      // Scroll to result on mobile
      if (window.innerWidth < 768) {
        setTimeout(() => {
          this.resultDisplay.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
    }
  }
}

/**
 * Initialize calculator
 */
export const initCalculator = () => {
  const calculators = document.querySelectorAll('.moving-calculator');

  calculators.forEach((calculator) => {
    new MovingCostCalculator(calculator);
  });
};
